import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import {
  availabilityNote,
  canShowEventPopup,
  deadlineNote,
  descriptionBlocks,
  discoveryBlocked,
  eventDetailPath,
  formatPrice,
  formatZurichDate,
  formatZurichTime,
  hasShownEventPopup,
  markEventPopupShown,
  registrationStatus,
  waitlistNote,
} from '../events/discovery'
import { useEventDiscovery } from '../events/usePublicEvents'
import './UpcomingEventsPopup.css'

function focusable(node) {
  if (!node) return []
  return [...node.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((item) => item.getAttribute('aria-hidden') !== 'true')
}

export default function UpcomingEventsPopup() {
  const location = useLocation()
  const { ready, slides, now } = useEventDiscovery(30000)
  const [open, setOpen] = useState(false)
  const [brokenImage, setBrokenImage] = useState(null)
  const [index, setIndex] = useState(0)
  const dialogRef = useRef(null)
  const closeRef = useRef(null)
  const bodyRef = useRef(null)
  const scrolls = useRef(new Map())
  const touch = useRef(null)
  const allowed = canShowEventPopup(location.pathname, location.search)
  const position = slides.length ? Math.min(index, slides.length - 1) : 0
  const slide = slides[position] || null

  useEffect(() => {
    if (!ready || open || hasShownEventPopup() || !allowed || slides.length === 0) return undefined
    let timer = 0
    const attempt = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        if (hasShownEventPopup() || discoveryBlocked() || slides.length === 0) return
        markEventPopupShown()
        setOpen(true)
      }, 350)
    }
    attempt()
    const id = window.setInterval(attempt, 1000)
    document.addEventListener('focusout', attempt)
    return () => {
      window.clearTimeout(timer)
      window.clearInterval(id)
      document.removeEventListener('focusout', attempt)
    }
  }, [open, ready, allowed, slides.length, location.pathname, location.search])

  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(slides.length - 1, 0)))
  }, [slides.length])

  useEffect(() => {
    if (!open) return undefined
    const previous = document.activeElement
    const root = document.documentElement
    const previousRoot = root.style.overflow
    const previousBody = document.body.style.overflow
    root.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const items = focusable(dialogRef.current)
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      root.style.overflow = previousRoot
      document.body.style.overflow = previousBody
      if (previous && previous.focus) previous.focus()
    }
  }, [open])

  useEffect(() => {
    if (!bodyRef.current || !slide) return
    bodyRef.current.scrollTop = scrolls.current.get(slide.id) || 0
  }, [slide])

  if (!open || !slide) return null
  const registration = registrationStatus(slide, now)
  const paragraphs = descriptionBlocks(slide.description)
  const price = formatPrice(slide.price_text)
  const places = availabilityNote(slide)
  const deadline = deadlineNote(slide)
  const waitlist = waitlistNote(slide)
  const several = slides.length > 1
  const href = eventDetailPath(slide)

  function close() {
    setOpen(false)
  }

  function rememberScroll(event) {
    scrolls.current.set(slide.id, event.currentTarget.scrollTop)
  }

  function onTouchStart(event) {
    const point = event.changedTouches[0]
    touch.current = { x: point.clientX, y: point.clientY }
  }

  function onTouchEnd(event) {
    if (!touch.current || !several) return
    const point = event.changedTouches[0]
    const dx = point.clientX - touch.current.x
    const dy = point.clientY - touch.current.y
    touch.current = null
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return
    setIndex((current) => {
      const next = dx < 0 ? current + 1 : current - 1
      return Math.min(Math.max(next, 0), slides.length - 1)
    })
  }

  return createPortal(
    <div className="event-popup-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) close() }}>
      <div
        ref={dialogRef}
        className="event-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upcoming-events-title"
        aria-describedby="upcoming-events-text"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="event-popup-head">
          <div>
            <h2 id="upcoming-events-title">Die nächsten Moveland Events</h2>
            <p id="upcoming-events-text">Entdecke, was als Nächstes stattfindet.</p>
          </div>
          <button ref={closeRef} type="button" className="event-popup-close" aria-label="Schliessen" onClick={close}>×</button>
        </div>
        <article key={slide.id} className="event-popup-slide" ref={bodyRef} onScroll={rememberScroll}>
          <Link className="event-popup-photo" to={href} onClick={close}>
            {slide.image_url && brokenImage !== slide.id
              ? <img src={slide.image_url} alt={slide.image_alt || slide.title} onError={() => setBrokenImage(slide.id)} />
              : <span className="event-popup-fallback">{slide.title}</span>}
          </Link>
          <div className="event-popup-content">
            <h3><Link to={href} onClick={close}>{slide.title}</Link></h3>
            <p className="event-popup-when">
              {formatZurichDate(slide.start_at)}
              {formatZurichTime(slide.start_at) ? ` · ${formatZurichTime(slide.start_at)}` : ''}
              {slide.end_at && formatZurichTime(slide.end_at) ? `–${formatZurichTime(slide.end_at)}` : ''}
            </p>
            {(slide.purpose_label || slide.difficulty_label) && (
              <p className="event-popup-marks">
                {slide.purpose_label && <span>{slide.purpose_label}</span>}
                {slide.difficulty_label && <span>{slide.difficulty_label}</span>}
              </p>
            )}
            {slide.purpose_copy && <p>{slide.purpose_copy}</p>}
            {slide.location && <p className="event-popup-place">{slide.location}</p>}
            {paragraphs.map((paragraph, paragraphIndex) => <p key={`${slide.id}-${paragraphIndex}`} className="event-popup-description">{paragraph}</p>)}
            <ul className="event-popup-facts">
              {price && <li>Preis: {price}</li>}
              <li className={`event-popup-status event-popup-status--${registration.tone}`}>{registration.label}</li>
              {deadline && <li>{deadline}</li>}
              {places && <li>{places}</li>}
              {waitlist && <li>{waitlist}</li>}
            </ul>
            <Link className="btn btn-primary event-popup-cta" to={href} onClick={close}>Event ansehen</Link>
          </div>
        </article>
        {several && (
          <div className="event-popup-nav">
            <button type="button" onClick={() => setIndex((current) => Math.max(0, current - 1))} disabled={position === 0} aria-label="Vorheriges Event">Zurück</button>
            <span aria-live="polite">{position + 1} von {slides.length}</span>
            <button type="button" onClick={() => setIndex((current) => Math.min(slides.length - 1, current + 1))} disabled={position >= slides.length - 1} aria-label="Nächstes Event">Weiter</button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
